import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { useAgent } from "@/lib/agentContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, ArrowRight, MessageCircle, ShieldCheck, BadgeCheck,
  Home as HomeIcon, MapPin, IndianRupee, CalendarClock, Loader2, Phone, User,
  CheckCircle2, Building2, Ruler, Layers, Sofa, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

// Resolve relative /api/... URLs to absolute for <img> tags in dev/preview
// (kubernetes ingress routes /api/* to backend, so this also works on prod
// where REACT_APP_BACKEND_URL == window.location.origin).
const resolveImg = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
};

const FURNISHING_LABEL = {
  "Unfurnished": "Unfurnished",
  "Semi-Furnished": "Semi-Furnished",
  "Fully Furnished": "Fully Furnished",
};

function DetailRow({ icon: Icon, label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-sg-line/60 last:border-b-0">
      <div className="w-9 h-9 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-sg-mute">{label}</div>
        <div className="text-sm font-medium text-sg-navy mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

function EnquiryForm({ rental, onSubmitted }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const agentFirst = (rental.agent_name || "the agent").split(" ")[0] || "the agent";

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2 || phone.replace(/\D/g, "").length < 10) {
      toast.error("Please share your name and a valid 10-digit phone.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rentals/${rental.rental_id}/enquire`, {
        name: name.trim(),
        phone: phone.trim(),
      });
      setDone(true);
      onSubmitted && onSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="sg-card p-8 text-center max-w-xl mx-auto" data-testid="rental-enquiry-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="font-display text-3xl text-sg-navy mt-4">Enquiry sent.</h3>
        <p className="text-sm text-sg-mute mt-2 max-w-sm mx-auto">
          {agentFirst} has been WhatsApp-notified with your details.
          Expect a call or WhatsApp reply within a few hours with the visit slot and full rent break-up.
        </p>
        <div className="mt-6 inline-flex gap-3">
          <Link to="/rent" className="pill-outline text-sm inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Browse more rentals
          </Link>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="sg-card p-6 md:p-8 max-w-xl mx-auto"
      data-testid="rental-enquiry-form"
    >
      <div className="overline text-sg-gold-deep">INTERESTED?</div>
      <h3 className="font-display text-3xl text-sg-navy mt-1">
        Share your details with <span className="font-serif-italic text-sg-gold-deep">{agentFirst}.</span>
      </h3>
      <p className="text-sm text-sg-mute mt-2">
        We&apos;ll WhatsApp {agentFirst} the moment you submit — no data broadcast, no cold callers.
      </p>
      <div className="mt-5 space-y-4">
        <div>
          <Label>Your name</Label>
          <div className="relative">
            <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sg-mute" />
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" className="pl-9" data-testid="rental-enquiry-name" />
          </div>
        </div>
        <div>
          <Label>Phone (WhatsApp)</Label>
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-sg-mute" />
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98xxxxxxxx" className="pl-9" data-testid="rental-enquiry-phone" />
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="w-full bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="rental-enquiry-submit">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
          Send my enquiry
        </Button>
        <p className="text-[11px] text-sg-mute text-center">
          Your number is shared only with the listing agent. Never broadcast to third parties.
        </p>
      </div>
    </form>
  );
}

export default function RentalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const agent = useAgent(); // non-null only inside /agent/:slug/... microsite
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    setLoading(true); setNotFound(false); setActivePhoto(0);
    api.get(`/rentals/${id}`)
      .then(({ data }) => setRental(data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load rental. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const hero = useMemo(() => {
    if (!rental?.images?.length) return null;
    return resolveImg(rental.images[activePhoto] || rental.images[0]);
  }, [rental, activePhoto]);

  const inner = (() => {
    if (loading) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center text-sg-mute" data-testid="rental-detail-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading rental…
        </div>
      );
    }
    if (notFound || !rental) {
      return (
        <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="rental-detail-notfound">
          <div className="text-center max-w-md">
            <HomeIcon className="w-12 h-12 mx-auto text-sg-gold-deep" />
            <h1 className="font-display text-4xl text-sg-navy mt-4">Rental not found.</h1>
            <p className="text-sm text-sg-mute mt-2">
              This listing may have been rented out or removed. Browse other verified rentals below.
            </p>
            <Button className="mt-5 bg-sg-navy text-white" onClick={() => navigate(agent ? `/agent/${agent.slug}/rent` : "/rent")} data-testid="rental-detail-back-to-list">
              Browse rentals
            </Button>
          </div>
        </div>
      );
    }

    const backLink = agent ? `/agent/${agent.slug}/rent` : "/rent";
    const monthlyRent = Number(rental.monthly_rent || 0);
    const deposit = Number(rental.security_deposit || 0);
    const agentFullName = rental.agent_name || "Our team";
    const agentFirst = agentFullName.split(" ")[0] || "there";

    return (
      <>
        {/* HEADER + GALLERY ────────────────────────────────────── */}
        <section className="bg-sg-navy text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-10 pb-6">
            <Link to={backLink} className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1" data-testid="rental-detail-back">
              <ArrowLeft className="w-4 h-4" /> Back to rentals
            </Link>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div className="overline text-sg-gold">RENTAL · {rental.locality?.toUpperCase()}</div>
              <div className="text-xs text-white/60 font-mono">{rental.rental_id}</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">
              {rental.bhk} · {rental.property_type} <span className="font-serif-italic text-sg-gold">in {rental.locality}.</span>
            </h1>
            <p className="text-white/70 text-sm mt-2 flex items-center gap-1">
              <MapPin className="w-4 h-4" /> {rental.address}
            </p>
          </div>

          {/* Gallery */}
          <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-10">
            {rental.images?.length ? (
              <div className="grid grid-cols-1 md:grid-cols-[3fr_1fr] gap-3">
                <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-white/5 border border-white/10">
                  {hero
                    ? <img src={hero} alt={rental.address} className="w-full h-full object-cover" data-testid="rental-detail-hero" />
                    : <div className="w-full h-full flex items-center justify-center text-white/50"><HomeIcon className="w-10 h-10" /></div>
                  }
                </div>
                <div className="grid grid-cols-4 md:grid-cols-1 gap-3">
                  {rental.images.slice(0, 4).map((u, i) => (
                    <button
                      key={i}
                      onClick={() => setActivePhoto(i)}
                      className={`aspect-square md:aspect-video rounded-xl overflow-hidden border-2 transition-colors ${activePhoto === i ? "border-sg-gold" : "border-white/10 hover:border-white/40"}`}
                      data-testid={`rental-detail-thumb-${i}`}
                      aria-label={`Show photo ${i + 1}`}
                    >
                      <img src={resolveImg(u)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="aspect-[16/6] rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-white/50">
                <HomeIcon className="w-10 h-10" />
              </div>
            )}
          </div>
        </section>

        {/* BODY ─────────────────────────────────────────────────── */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
          <div>
            {/* Headline stat row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <div className="sg-card-cream text-center">
                <div className="text-[11px] uppercase text-sg-mute tracking-wider">Monthly Rent</div>
                <div className="font-display text-2xl text-sg-navy mt-1">
                  <IndianRupee className="w-4 h-4 inline -mt-1" />{monthlyRent.toLocaleString("en-IN")}
                </div>
              </div>
              <div className="sg-card-cream text-center">
                <div className="text-[11px] uppercase text-sg-mute tracking-wider">Security Deposit</div>
                <div className="font-display text-2xl text-sg-navy mt-1">
                  <IndianRupee className="w-4 h-4 inline -mt-1" />{deposit ? deposit.toLocaleString("en-IN") : "—"}
                </div>
              </div>
              <div className="sg-card-cream text-center">
                <div className="text-[11px] uppercase text-sg-mute tracking-wider">Furnishing</div>
                <div className="font-display text-lg text-sg-navy mt-1">{FURNISHING_LABEL[rental.furnishing] || rental.furnishing}</div>
              </div>
              <div className="sg-card-cream text-center">
                <div className="text-[11px] uppercase text-sg-mute tracking-wider">Available From</div>
                <div className="font-display text-lg text-sg-navy mt-1">{rental.available_from}</div>
              </div>
            </div>

            {/* About this rental */}
            <div className="mb-10">
              <div className="overline text-sg-gold-deep mb-2">ABOUT THIS RENTAL</div>
              <h2 className="font-display text-3xl text-sg-navy">The essentials.</h2>
              {rental.description ? (
                <p className="text-sm text-sg-navy/80 mt-4 leading-relaxed whitespace-pre-line" data-testid="rental-detail-description">
                  {rental.description}
                </p>
              ) : (
                <p className="text-sm text-sg-mute mt-4 italic">The agent hasn&apos;t added a description yet. WhatsApp them below for more highlights.</p>
              )}
            </div>

            {/* Property details grid */}
            <div className="mb-10">
              <div className="overline text-sg-gold-deep mb-2">PROPERTY DETAILS</div>
              <h2 className="font-display text-3xl text-sg-navy">What you&apos;re renting.</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 mt-5 bg-white border border-sg-line rounded-2xl p-5" data-testid="rental-detail-facts">
                <DetailRow icon={HomeIcon} label="Property Type" value={rental.property_type} />
                <DetailRow icon={Building2} label="Configuration" value={rental.bhk} />
                <DetailRow icon={Sofa} label="Furnishing" value={FURNISHING_LABEL[rental.furnishing] || rental.furnishing} />
                <DetailRow icon={Ruler} label="Carpet Area" value={rental.area_sqft ? `${rental.area_sqft.toLocaleString("en-IN")} sq.ft.` : null} />
                <DetailRow icon={Layers} label="Floor" value={rental.floor_number ? `${rental.floor_number}${rental.total_floors ? ` of ${rental.total_floors}` : ""}` : null} />
                <DetailRow icon={MapPin} label="Locality" value={rental.locality} />
                <DetailRow icon={MapPin} label="Address" value={rental.address} />
                <DetailRow icon={CalendarClock} label="Available From" value={rental.available_from} />
              </div>
            </div>

            {/* Agent card */}
            <div className="mb-10">
              <div className="overline text-sg-gold-deep mb-2">LISTED BY</div>
              <div className="sg-card p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep flex-shrink-0">
                  <User className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-2xl text-sg-navy">{agentFullName}</div>
                  <div className="text-sm text-sg-mute mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5 text-sg-gold-deep" /> MahaCRITI-registered agent</span>
                    <span>·</span>
                    <Link to={`/agent/${rental.agent_slug}`} className="hover:text-sg-navy underline underline-offset-2">View agent portal</Link>
                  </div>
                </div>
                <a
                  href={`https://wa.me/${(rental.agent_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${agentFirst}, I'm interested in the ${rental.bhk} ${rental.property_type} you have in ${rental.locality} (₹${monthlyRent.toLocaleString("en-IN")}/mo, listing ${rental.rental_id}). Can we schedule a visit?`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="pill-solid inline-flex items-center gap-2 whitespace-nowrap"
                  data-testid="rental-detail-wa-agent"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp {agentFirst}
                </a>
              </div>
            </div>
          </div>

          {/* Right rail — sticky trust panel */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-3">
              <div className="sg-card p-5">
                <div className="overline text-sg-gold-deep">TRUST</div>
                <h3 className="font-display text-xl text-sg-navy mt-1">Why this rental is verified</h3>
                <ul className="mt-3 space-y-3 text-sm">
                  <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-sg-gold-deep mt-0.5" /> Posted by a MahaCRITI-registered agent, not a scraped feed.</li>
                  <li className="flex gap-2"><Sparkles className="w-4 h-4 text-sg-gold-deep mt-0.5" /> Agent updates status the day it rents — no stale phantom leads.</li>
                  <li className="flex gap-2"><BadgeCheck className="w-4 h-4 text-sg-gold-deep mt-0.5" /> Your number reaches only the listing agent — never broadcast.</li>
                </ul>
              </div>
              <div className="sg-card-cream">
                <div className="text-xs text-sg-mute">Listing ID</div>
                <div className="font-mono text-sm text-sg-navy break-all">{rental.rental_id}</div>
                <div className="text-xs text-sg-mute mt-3">Posted</div>
                <div className="text-sm text-sg-navy">{(rental.created_at || "").slice(0, 10)}</div>
              </div>
            </div>
          </aside>
        </section>

        {/* ENQUIRY FORM (footer, not modal) ──────────────────────── */}
        <section id="enquire" className="bg-white border-t border-sg-line py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <div className="overline text-sg-gold-deep">CONTACT THE AGENT</div>
              <h2 className="font-display text-4xl text-sg-navy mt-2">
                Ready to visit? <span className="font-serif-italic text-sg-gold-deep">Leave your number.</span>
              </h2>
              <p className="text-sm text-sg-mute mt-3">
                {agentFirst} gets a WhatsApp alert the moment you submit — no callbacks queued in a portal inbox.
              </p>
            </div>
            <EnquiryForm rental={rental} />
            <div className="text-center mt-6">
              <Link to={agent ? `/agent/${agent.slug}/rent` : "/rent"} className="text-sm text-sg-navy hover:text-sg-gold-deep inline-flex items-center gap-1">
                Or browse more rentals <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  })();

  return agent ? inner : <PublicLayout>{inner}</PublicLayout>;
}
